import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { 
  ChevronDown, 
  Play, 
  Mail, 
  MapPin, 
  Facebook, 
  Instagram, 
  Linkedin, 
  Youtube,
  Sparkles,
  Heart,
  Star,
  BookOpen,
  Quote,
  ExternalLink,
  Download,
  FileText,
  Image as ImageIcon,
  Video
} from "lucide-react";

// Images from design guidelines and original site
const IMAGES = {
  heroBackground: "https://static.prod-images.emergentagent.com/jobs/54d4b667-a46d-48c9-b8e6-256d18f730df/images/cab2b61955c08548592876ca31120de93896363773275dec68bf6c3f0c4efd42.png",
  mentorPortrait: "https://gillesdelieuze.com/wp-content/uploads/2024/03/IMG_0258_www.vincentphotographie.com_-1024x683.jpg",
  mentorPortrait2: "https://gillesdelieuze.com/wp-content/uploads/2024/03/IMG_0027_www.vincentphotographie.com_-300x300.jpg",
  journeyDolphins: "https://static.prod-images.emergentagent.com/jobs/54d4b667-a46d-48c9-b8e6-256d18f730df/images/3736417b270a0375c50f2a10be3ce89126c9053f076db75a35a9f54fd3ec9884.png",
  // Vraie couverture de L'Oracle des Maîtres Dauphins (Éditions Tredaniel/Véga)
  bookOracle: "https://www.editions-tredaniel.com/images/CouverturesPresse/978_2_38135_501_6_HiRes.jpg",
  // Couverture Humain_Divin depuis Le Grand Changement
  bookHumainDivin: "https://legrandchangement.com/wp-content/uploads/2015/06/humain-divin.jpg",
  dolphinsOcean: "https://images.unsplash.com/photo-1562742329-44b5a9a34dcc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwyfHxkb2xwaGlucyUyMHVuZGVyd2F0ZXIlMjBvY2VhbnxlbnwwfHx8fDE3NzU4MTUxMjV8MA&ixlib=rb-4.1.0&q=85",
  whale: "https://images.pexels.com/photos/4761513/pexels-photo-4761513.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  childhood: "https://gillesdelieuze.com/wp-content/uploads/2022/11/une-enfance-connectee-600x710-1-254x300.jpg",
  logo: "https://gillesdelieuze.com/wp-content/uploads/2022/12/Logo_Final_3bd-.png"
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: "easeOut" } }
};

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav 
      className={`nav-fixed ${scrolled ? 'nav-scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8 }}
      data-testid="navigation"
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <a href="#hero" data-testid="nav-logo">
          <img src={IMAGES.logo} alt="Gilles Delieuze" className="h-10 md:h-12" />
        </a>
        <div className="hidden md:flex items-center gap-8">
          <a href="#about" className="text-text-muted hover:text-primary transition-colors" data-testid="nav-about">À Propos</a>
          <a href="#books" className="text-text-muted hover:text-primary transition-colors" data-testid="nav-books">Livres</a>
          <a href="#press" className="text-text-muted hover:text-primary transition-colors" data-testid="nav-press">Presse</a>
          <a href="#journey" className="text-text-muted hover:text-primary transition-colors" data-testid="nav-journey">Parcours</a>
          <a href="#contact" className="btn-primary text-sm" data-testid="nav-contact">Contact</a>
        </div>
      </div>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);

  return (
    <section id="hero" className="hero-section" data-testid="hero-section">
      <motion.div className="hero-bg" style={{ y }}>
        <img src={IMAGES.heroBackground} alt="Ocean depths" />
      </motion.div>
      
      <motion.div 
        className="hero-content"
        style={{ opacity }}
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <motion.div variants={fadeInUp} className="mb-6">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-dark text-sm text-text-muted">
            <Sparkles size={16} className="text-primary" />
            Auteur • Enseignant • Mentor
          </span>
        </motion.div>
        
        <motion.h1 
          variants={fadeInUp}
          className="text-hero font-serif mb-6"
        >
          <span className="text-gradient-gold">Gilles Delieuze</span>
        </motion.h1>
        
        <motion.p 
          variants={fadeInUp}
          className="text-xl md:text-2xl text-text-muted mb-8 max-w-2xl mx-auto font-light"
        >
          Votre compagnon dans l'éveil de votre essence véritable
        </motion.p>
        
        <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="#about" className="btn-primary" data-testid="hero-discover-btn">
            Découvrir mon parcours
            <ChevronDown size={20} />
          </a>
          <a href="#videos" className="btn-outline" data-testid="hero-videos-btn">
            <Play size={20} />
            Voir mes interviews
          </a>
        </motion.div>
        
        <motion.div 
          variants={fadeInUp}
          className="mt-16"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ChevronDown size={32} className="text-primary mx-auto" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const imageY = useTransform(scrollYProgress, [0, 1], [100, -100]);

  return (
    <section id="about" ref={ref} className="section-padding relative" data-testid="about-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeInLeft}
          >
            <motion.div 
              className="relative rounded-2xl overflow-hidden image-glow"
              style={{ y: imageY }}
            >
              <img 
                src={IMAGES.mentorPortrait} 
                alt="Gilles Delieuze"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
            </motion.div>
          </motion.div>
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            <motion.span variants={fadeInUp} className="text-primary uppercase tracking-widest text-sm">
              À Propos
            </motion.span>
            
            <motion.h2 variants={fadeInUp} className="text-section font-serif mt-4 mb-8">
              <span className="text-gradient-gold">27 ans</span> au service de votre épanouissement
            </motion.h2>
            
            <motion.p variants={fadeInUp} className="text-lg text-text-muted mb-6 leading-relaxed">
              Auteur de deux bestsellers, je tisse des ponts entre vous et votre véritable essence. 
              Mon approche unique allie <span className="text-primary">profondeur de conscience</span>, 
              précision du raisonnement et magie de la résonance.
            </motion.p>
            
            <motion.p variants={fadeInUp} className="text-lg text-text-muted mb-8 leading-relaxed">
              Artiste dans l'âme, je navigue entre l'art, la philosophie, les neurosciences et la mécanique quantique 
              pour vous offrir une vision holistique de l'existence et révéler vos talents cachés.
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="grid grid-cols-3 gap-6 p-6 rounded-xl glass-dark"
            >
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-serif text-gradient-gold">27+</div>
                <div className="text-sm text-text-muted mt-1">Années d'expérience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-serif text-gradient-gold">2</div>
                <div className="text-sm text-text-muted mt-1">Bestsellers</div>
              </div>
              <div className="text-center">
                <div className="text-3xl md:text-4xl font-serif text-gradient-gold">∞</div>
                <div className="text-sm text-text-muted mt-1">Âmes guidées</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Quote Section
const QuoteSection = () => (
  <section className="quote-section" data-testid="quote-section">
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={scaleIn}
      className="max-w-4xl mx-auto relative z-10"
    >
      <Quote size={48} className="text-primary mb-8 mx-auto opacity-50" />
      <blockquote className="text-2xl md:text-4xl font-serif italic text-text-main leading-relaxed mb-8">
        L'univers est une <span className="text-gradient-gold">symphonie</span> qui ne peut exister sans votre note propre.
      </blockquote>
      <cite className="text-primary not-italic">— Gilles Delieuze</cite>
    </motion.div>
  </section>
);

// Books Section
const BooksSection = () => {
  const books = [
    {
      title: "L'Oracle des Maîtres Dauphins",
      description: "Un voyage initiatique au cœur de la sagesse des dauphins. Découvrez les 13 fréquences qui transformeront votre rapport à l'existence.",
      image: IMAGES.bookOracle,
      badge: "Bestseller"
    },
    {
      title: "Humain_Divin",
      description: "Une invitation à éveiller votre discernement et votre clairvoyance, vous libérant des conditionnements pour explorer un champ des possibles infini.",
      image: IMAGES.bookHumainDivin,
      badge: "Bestseller"
    }
  ];

  return (
    <section id="books" className="section-padding bg-surface/30" data-testid="books-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span variants={fadeInUp} className="text-primary uppercase tracking-widest text-sm">
            Publications
          </motion.span>
          <motion.h2 variants={fadeInUp} className="text-section font-serif mt-4">
            Mes <span className="text-gradient-gold">Bestsellers</span>
          </motion.h2>
        </motion.div>
        
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {books.map((book, index) => (
            <motion.div
              key={book.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={index === 0 ? fadeInLeft : fadeInRight}
              className="book-card glass-dark p-6 md:p-8"
              data-testid={`book-card-${index}`}
            >
              <div className="flex flex-col md:flex-row gap-6">
                <div className="relative w-full md:w-48 flex-shrink-0">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden glow-gold">
                    <img 
                      src={book.image} 
                      alt={book.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="absolute -top-3 -right-3 px-3 py-1 bg-primary text-background text-xs font-bold rounded-full">
                    {book.badge}
                  </span>
                </div>
                
                <div className="flex-1">
                  <h3 className="text-2xl font-serif mb-4 text-text-main">{book.title}</h3>
                  <p className="text-text-muted mb-6 leading-relaxed">{book.description}</p>
                  <div className="flex items-center gap-1 text-primary">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} fill="currentColor" />
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Press Section with Marquee
const PressSection = () => {
  const pressItems = [
    {
      source: "Magazine Vague(s)",
      quote: "Un homme qui ose en toute liberté être lui-même et poursuit son chemin en écoutant son cœur.",
      description: "Sans posture de « sachant », ni retenue. Il a l'expression fluide, de celle qui ne cherche ni à convaincre, ni à prendre. Il est là, répond aux questions avec une attention particulière et une humilité toute douce qui est celle des sages.",
      link: "https://www.vagues-aude.fr/2018/07/20/dossier-vague-s-juillet-ao%C3%BBt-gilles-delieuze-homme-libre/"
    },
    {
      source: "FémininBio",
      quote: "Ancrer sa spiritualité dans l'instant présent",
      description: "Découvrez comment vivre une spiritualité incarnée au quotidien.",
      link: "https://www.femininbio.com/spiritualite/inspirations/ancrer-sa-spiritualite-dans-linstant-present-mise-en-pratique-61054"
    }
  ];

  return (
    <section id="press" className="section-padding" data-testid="press-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span variants={fadeInUp} className="text-primary uppercase tracking-widest text-sm">
            Ils parlent de moi
          </motion.span>
          <motion.h2 variants={fadeInUp} className="text-section font-serif mt-4">
            Revue de <span className="text-gradient-gold">Presse</span>
          </motion.h2>
        </motion.div>
        
        {/* Marquee */}
        <div className="marquee-container mb-16 py-8 border-y border-white/5">
          <div className="marquee-content">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-center gap-16">
                <span className="text-4xl md:text-6xl font-serif text-white/10 whitespace-nowrap">Magazine Vague(s)</span>
                <Star className="text-primary" size={24} />
                <span className="text-4xl md:text-6xl font-serif text-white/10 whitespace-nowrap">FémininBio</span>
                <Star className="text-primary" size={24} />
              </div>
            ))}
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8">
          {pressItems.map((item, index) => (
            <motion.div
              key={item.source}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={index === 0 ? fadeInLeft : fadeInRight}
              className="glass-gold p-8 rounded-2xl"
              data-testid={`press-card-${index}`}
            >
              <div className="flex items-center gap-3 mb-6">
                <BookOpen className="text-primary" size={24} />
                <h3 className="text-xl font-serif text-primary">{item.source}</h3>
              </div>
              
              <blockquote className="text-2xl font-serif italic mb-4 text-text-main leading-relaxed">
                "{item.quote}"
              </blockquote>
              
              <p className="text-text-muted mb-6">{item.description}</p>
              
              <a 
                href={item.link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="link-underline inline-flex items-center gap-2"
                data-testid={`press-link-${index}`}
              >
                Lire l'article <ExternalLink size={16} />
              </a>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Videos Section
const VideosSection = () => {
  const [showMoreVideos, setShowMoreVideos] = useState(false);
  
  const mainVideos = [
    {
      id: "BVUIuenK-iE",
      title: "Changer de regard",
      channel: "Tistrya - 555K abonnés"
    },
    {
      id: "OsD3-TVuURI",
      title: "Vivre une SPIRITUALITÉ INCARNÉE",
      channel: "ABC TALK TV - 283K abonnés"
    },
    {
      id: "8aAF9ev4MJU",
      title: "Vivez votre potentiel illimité",
      channel: "On Ne Vous Demande Pas d'y Croire"
    },
    {
      id: "xnzEPd5goa0",
      title: "Transforme ta vie avec les fréquences des dauphins",
      channel: "Asso LUMINAME"
    }
  ];
  
  const moreVideos = [
    {
      id: "pQaphI0wGD4",
      title: "Les Maîtres Dauphins, introduction aux 14 fréquences",
      channel: "Le Grand Changement TV"
    },
    {
      id: "4PIa-6p3mrg",
      title: "Une autre approche de Ho'oponopono",
      channel: "101K vues"
    },
    {
      id: "LvT-FVO9sQY",
      title: "Vers une spiritualité incarnée",
      channel: "Charlotte Hoefman - 21K vues"
    },
    {
      id: "ahRGfTFR37g",
      title: "Un regard Révolutionnaire sur les Mémoires et Karma",
      channel: "Rayonnance TV"
    }
  ];
  
  const playlists = [
    {
      title: "Interviews & Conférences",
      description: "47 vidéos - Retrouvez toutes mes interviews et conférences",
      url: "https://www.youtube.com/playlist?list=PLPoQwaDxSeMIpI3qvq74uNpClW5mF1HWT",
      videoCount: "47 vidéos"
    },
    {
      title: "Enseignements & Méditations",
      description: "Collection de guidances et méditations",
      url: "https://www.youtube.com/playlist?list=PLPoQwaDxSeMIvrMZpFPK50bczoxrl8bUu",
      videoCount: "Playlist complète"
    }
  ];

  return (
    <section id="videos" className="section-padding bg-surface/30" data-testid="videos-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span variants={fadeInUp} className="text-primary uppercase tracking-widest text-sm">
            Médias
          </motion.span>
          <motion.h2 variants={fadeInUp} className="text-section font-serif mt-4">
            <span className="text-gradient-gold">Interviews</span> & Conférences
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-text-muted mt-4 max-w-2xl mx-auto">
            Découvrez mes échanges sur la spiritualité incarnée, les dauphins et la transformation personnelle.
          </motion.p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 gap-8">
          {mainVideos.map((video, index) => (
            <motion.div
              key={video.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ delay: index * 0.1 }}
              data-testid={`video-card-${index}`}
            >
              <div className="video-container glow-gold">
                <iframe
                  src={`https://www.youtube.com/embed/${video.id}`}
                  title={video.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-serif text-text-main">{video.title}</h3>
                <p className="text-sm text-text-muted mt-1">{video.channel}</p>
              </div>
            </motion.div>
          ))}
        </div>
        
        {/* Tiroir pour plus de vidéos */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="mt-12"
        >
          <button
            onClick={() => setShowMoreVideos(!showMoreVideos)}
            className="w-full py-4 px-6 glass-dark rounded-xl flex items-center justify-between hover:border-primary/30 border border-transparent transition-all group"
            data-testid="show-more-videos-btn"
          >
            <span className="text-lg font-serif">
              {showMoreVideos ? "Masquer les vidéos supplémentaires" : "Voir plus de vidéos"}
            </span>
            <ChevronDown 
              size={24} 
              className={`text-primary transition-transform duration-300 ${showMoreVideos ? 'rotate-180' : ''}`}
            />
          </button>
          
          <AnimatePresence>
            {showMoreVideos && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="overflow-hidden"
              >
                <div className="grid md:grid-cols-2 gap-8 pt-8">
                  {moreVideos.map((video, index) => (
                    <motion.div
                      key={video.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      data-testid={`more-video-card-${index}`}
                    >
                      <div className="video-container glow-gold">
                        <iframe
                          src={`https://www.youtube.com/embed/${video.id}`}
                          title={video.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                      <div className="mt-4">
                        <h3 className="text-xl font-serif text-text-main">{video.title}</h3>
                        <p className="text-sm text-text-muted mt-1">{video.channel}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
        
        {/* Section Playlists YouTube */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="mt-16"
        >
          <motion.h3 variants={fadeInUp} className="text-2xl font-serif text-center mb-8">
            <span className="text-gradient-gold">Playlists</span> YouTube
          </motion.h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            {playlists.map((playlist, index) => (
              <motion.a
                key={playlist.url}
                href={playlist.url}
                target="_blank"
                rel="noopener noreferrer"
                variants={fadeInUp}
                className="glass-gold p-6 rounded-xl flex items-center gap-4 hover:scale-[1.02] transition-transform group"
                data-testid={`playlist-card-${index}`}
              >
                <div className="w-16 h-16 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0">
                  <Play size={28} className="text-white ml-1" fill="white" />
                </div>
                <div className="flex-1">
                  <h4 className="text-lg font-medium text-text-main group-hover:text-primary transition-colors">
                    {playlist.title}
                  </h4>
                  <p className="text-sm text-text-muted mt-1">{playlist.description}</p>
                  <span className="text-xs text-primary mt-2 inline-flex items-center gap-1">
                    <Youtube size={14} />
                    {playlist.videoCount}
                  </span>
                </div>
                <ExternalLink size={20} className="text-text-muted group-hover:text-primary transition-colors" />
              </motion.a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Press Kit Section (Dossier de Presse)
const PressKitSection = () => {
  const flipbookUrl = "https://gillesdelieuze.com/?r3d=10629";
  
  return (
    <section id="presskit" className="section-padding" data-testid="presskit-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-12"
        >
          <motion.span variants={fadeInUp} className="text-primary uppercase tracking-widest text-sm">
            Médias & Journalistes
          </motion.span>
          <motion.h2 variants={fadeInUp} className="text-section font-serif mt-4">
            Dossier de <span className="text-gradient-gold">Presse</span>
          </motion.h2>
        </motion.div>
        
        {/* Aperçu et accès au Flipbook */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="mb-12"
        >
          <a 
            href={flipbookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full rounded-2xl overflow-hidden bg-gradient-to-br from-surface via-surface/80 to-primary/10 border border-primary/20 hover:border-primary/40 transition-all group cursor-pointer"
            data-testid="flipbook-link"
          >
            <div className="flex flex-col items-center justify-center py-16 px-8">
              <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 group-hover:scale-110 transition-all">
                <BookOpen className="w-12 h-12 text-primary" />
              </div>
              <h3 className="text-2xl font-serif mb-3 group-hover:text-primary transition-colors">
                Consulter le Press-Book
              </h3>
              <p className="text-text-muted text-center max-w-md mb-6">
                Feuilletez le dossier de presse interactif avec toutes les informations, photos et articles.
              </p>
              <span className="inline-flex items-center gap-2 px-6 py-3 bg-primary/10 rounded-full text-primary font-medium group-hover:bg-primary group-hover:text-background transition-all">
                <ExternalLink size={18} />
                Ouvrir le flipbook
              </span>
            </div>
          </a>
        </motion.div>
        
        {/* Download CTA */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="text-center"
        >
          <div className="glass-gold p-8 rounded-2xl max-w-xl mx-auto">
            <Download className="w-10 h-10 text-primary mx-auto mb-4" />
            <h3 className="text-xl font-serif mb-4">Télécharger le dossier complet</h3>
            <a 
              href={flipbookUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex"
              data-testid="presskit-download-btn"
            >
              <Download size={20} />
              Télécharger le dossier de presse
            </a>
            <p className="text-sm text-text-muted mt-4">
              Contact presse : <a href="mailto:gilles@centre-terre-ciel.com" className="text-primary hover:underline">gilles@centre-terre-ciel.com</a>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Journey Section with Accordion
const JourneySection = () => {
  const [openItem, setOpenItem] = useState(null);
  
  const journeySteps = [
    {
      id: "enfance",
      title: "Une Enfance Connectée",
      period: "Premières années",
      content: "Dès mon plus jeune âge, ma conscience des mondes invisibles et ma perception des énergies m'ont accompagné. À 8 ans, je me plongeai dans l'étude de la radiesthésie, de la cartomancie et du bouddhisme. Cette quête précoce était celle d'une âme ancienne à la recherche de la sagesse universelle."
    },
    {
      id: "appel",
      title: "L'Appel",
      period: "2008",
      content: "À la croisée des chemins entre ma carrière dans l'accompagnement social et ma vie spirituelle, un appel inattendu a surgi. L'invitation à unifier ces deux mondes m'a poussé à me consacrer entièrement à cette expression de la Vie qui m'habitait."
    },
    {
      id: "dauphins",
      title: "La Rencontre avec les Dauphins",
      period: "2007-2010",
      content: "« Les dauphins t'attendent, ils ont un message à te délivrer ». Cette phrase s'est imposée avec une clarté déconcertante. Réveillé à 3h33 par les Maîtres Dauphins, j'ai commencé à recevoir des enseignements nocturnes qui ont bouleversé mon existence et donné naissance à l'Oracle."
    },
    {
      id: "baleine",
      title: "La Rencontre avec la Baleine",
      period: "2010",
      content: "Sur les terres de la Réunion, une rencontre d'une intensité exceptionnelle. La baleine m'invitait à la rejoindre, honorant un rendez-vous d'âme à âme qui marquait le début d'une réunion des différentes parties de mon Soi."
    },
    {
      id: "moorea",
      title: "L'Appel de Mooréa",
      period: "Période de transformation",
      content: "Guidé par la réponse « MOOREA », je suis parti explorer cette île en forme de cœur. Les raies m'ont transmis des messages profonds sur le langage des étoiles et notre connexion avec l'univers."
    },
    {
      id: "quantique",
      title: "L'Ouverture au Quantique",
      period: "2014 - Présent",
      content: "J'ai commencé à recevoir des séquences de codes chiffrés révélant les dynamiques quantiques. Mes enseignements visent désormais à réconcilier le spirituel et le matériel, invitant chacun à vivre une spiritualité pleinement incarnée."
    }
  ];

  return (
    <section id="journey" className="section-padding" data-testid="journey-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-5 gap-12">
          {/* Sticky Image */}
          <div className="lg:col-span-2">
            <div className="sticky-sidebar">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInLeft}
              >
                <span className="text-primary uppercase tracking-widest text-sm">Mon Histoire</span>
                <h2 className="text-section font-serif mt-4 mb-8">
                  Un <span className="text-gradient-gold">Parcours</span> Singulier
                </h2>
                
                <div className="relative rounded-2xl overflow-hidden animate-float">
                  <img 
                    src={IMAGES.journeyDolphins} 
                    alt="Spiritual dolphin"
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                </div>
              </motion.div>
            </div>
          </div>
          
          {/* Accordion */}
          <div className="lg:col-span-3">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {journeySteps.map((step, index) => (
                <motion.div
                  key={step.id}
                  variants={fadeInUp}
                  className="accordion-item"
                  data-testid={`journey-item-${index}`}
                >
                  <button
                    className="accordion-trigger"
                    onClick={() => setOpenItem(openItem === step.id ? null : step.id)}
                    data-testid={`journey-trigger-${index}`}
                  >
                    <div>
                      <span className="text-sm text-primary block mb-1">{step.period}</span>
                      <span className="text-2xl">{step.title}</span>
                    </div>
                    <ChevronDown 
                      size={24} 
                      className={`text-primary transition-transform duration-300 ${openItem === step.id ? 'rotate-180' : ''}`}
                    />
                  </button>
                  
                  <AnimatePresence>
                    {openItem === step.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <p className="text-text-muted pb-6 leading-relaxed">
                          {step.content}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Values Section
const ValuesSection = () => {
  const values = [
    { icon: Heart, label: "Authenticité" },
    { icon: Sparkles, label: "Simplicité" },
    { icon: Star, label: "Profondeur" },
    { icon: Play, label: "Humour" },
    { icon: Heart, label: "Joie" },
    { icon: Heart, label: "Bienveillance" },
    { icon: Star, label: "Éthique" },
    { icon: Sparkles, label: "Précurseur" }
  ];

  return (
    <section className="section-padding bg-surface/30" data-testid="values-section">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-16"
        >
          <motion.span variants={fadeInUp} className="text-primary uppercase tracking-widest text-sm">
            Ce qui me guide
          </motion.span>
          <motion.h2 variants={fadeInUp} className="text-section font-serif mt-4">
            Mes <span className="text-gradient-gold">Valeurs</span>
          </motion.h2>
        </motion.div>
        
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6"
        >
          {values.map((value, index) => (
            <motion.div
              key={value.label}
              variants={scaleIn}
              className="value-card text-center"
              data-testid={`value-card-${index}`}
            >
              <value.icon className="w-8 h-8 text-primary mx-auto mb-4" />
              <span className="text-lg font-serif">{value.label}</span>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <section id="contact" className="section-padding" data-testid="contact-section">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center"
        >
          <motion.span variants={fadeInUp} className="text-primary uppercase tracking-widest text-sm">
            Restons connectés
          </motion.span>
          <motion.h2 variants={fadeInUp} className="text-section font-serif mt-4 mb-4">
            <span className="text-gradient-gold">Rejoignez</span> la communauté
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-text-muted mb-12 max-w-2xl mx-auto">
            Recevez mes inspirations, actualités et invitations à des événements exclusifs directement dans votre boîte mail.
          </motion.p>
          
          <motion.form 
            variants={fadeInUp}
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto mb-16"
          >
            <input
              type="text"
              placeholder="Votre prénom"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="contact-input flex-1"
              required
              data-testid="contact-name-input"
            />
            <input
              type="email"
              placeholder="Votre email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="contact-input flex-1"
              required
              data-testid="contact-email-input"
            />
            <button type="submit" className="btn-primary whitespace-nowrap" data-testid="contact-submit-btn">
              {submitted ? "Merci !" : "S'inscrire"}
              <Mail size={18} />
            </button>
          </motion.form>
          
          <motion.div variants={fadeInUp} className="glass-dark rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div>
                <h3 className="text-xl font-serif text-primary mb-4">Contact</h3>
                <div className="flex items-start gap-3 text-text-muted">
                  <MapPin className="text-primary mt-1 flex-shrink-0" size={20} />
                  <div>
                    <p className="font-medium text-text-main">Espace Asanoha</p>
                    <p>7 Rue de l'Église</p>
                    <p>11200 Ornaisons, France</p>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-xl font-serif text-primary mb-4">Suivez-moi</h3>
                <div className="flex gap-4">
                  <a 
                    href="https://www.facebook.com/Gdelieuze/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full glass-dark flex items-center justify-center hover:bg-primary/20 transition-colors"
                    data-testid="social-facebook"
                  >
                    <Facebook size={20} className="text-text-muted hover:text-primary" />
                  </a>
                  <a 
                    href="https://www.instagram.com/gilles_delieuze/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full glass-dark flex items-center justify-center hover:bg-primary/20 transition-colors"
                    data-testid="social-instagram"
                  >
                    <Instagram size={20} className="text-text-muted hover:text-primary" />
                  </a>
                  <a 
                    href="https://www.linkedin.com/in/gilles-delieuze-830544a2/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full glass-dark flex items-center justify-center hover:bg-primary/20 transition-colors"
                    data-testid="social-linkedin"
                  >
                    <Linkedin size={20} className="text-text-muted hover:text-primary" />
                  </a>
                  <a 
                    href="https://www.youtube.com/channel/UCzJ61lnyiUJmZJwuoH7jlEA" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full glass-dark flex items-center justify-center hover:bg-primary/20 transition-colors"
                    data-testid="social-youtube"
                  >
                    <Youtube size={20} className="text-text-muted hover:text-primary" />
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer className="py-8 border-t border-white/5" data-testid="footer">
    <div className="max-w-7xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
      <img src={IMAGES.logo} alt="Gilles Delieuze" className="h-8" />
      <p className="text-text-muted text-sm">© 2024 Gilles Delieuze. Tous droits réservés.</p>
      <div className="flex gap-6 text-sm text-text-muted">
        <a href="#" className="hover:text-primary transition-colors">Mentions légales</a>
        <a href="#" className="hover:text-primary transition-colors">Confidentialité</a>
        <a href="#" className="hover:text-primary transition-colors">CGV</a>
      </div>
    </div>
  </footer>
);

// Main App
function App() {
  return (
    <div className="App min-h-screen bg-background">
      <Navigation />
      <main>
        <HeroSection />
        <AboutSection />
        <QuoteSection />
        <BooksSection />
        <PressSection />
        <VideosSection />
        <PressKitSection />
        <JourneySection />
        <ValuesSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
